import React, { useState, useCallback, useEffect } from "react";
import useStyles from "./style";
import {
  Button,
  Grid,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { EditOutlined, DeleteOutline, AddLocationAltOutlined, ChevronLeft, ChevronRight } from "@mui/icons-material";
import _ from "lodash";

import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import { useDispatch, useSelector } from "react-redux";

//Services
import LocationApi from "Services/Location";

//Modals
import RestrictedLocationUpdateModal from "Pages/Modals/Location/RestrictLocation/Update";
import RestrictedLocationAddModal from "Pages/Modals/Location/RestrictLocation/Create";
import DeleteLocationModal from "Pages/Modals/Location/RestrictLocation/Delete";

const RestrictLocation = () => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const demoaccount_id = useSelector(
    (state) => state?.AuthReducer?.demoaccount_id || ""
  );
  
  const [row, setRow] = useState([]);
  
  //Pagination (simulated client-side since API doesn't seem to paginate this specific one, but we'll adapt to standard table)
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);

  const [RestrictLocationAddModal, setRestrictLocationAddModal] = useState(false);
  const [RestrictLocationUpdateModal, setRestrictLocationUpdateModal] = useState(false);
  const [deleteLocationModal, setdeletelocationModal] = useState(false);
  const [locationId, setLocationId] = useState(null);

  //Modals
  const handleOpenUpdateRestrictServiceModal = (rowData) => {
    setLocationId(rowData?._id);
    setRestrictLocationUpdateModal(true);
  };
  const handleCloseUpdateRestrictServiceModal = () => {
    setRestrictLocationUpdateModal(false);
    getRestrictLocationList();
  };

  const handleOpenAddRestrictServiceModal = () => {
    setRestrictLocationAddModal(true);
  };
  const handleCloseAddRestrictServiceModal = () => {
    setRestrictLocationAddModal(false);
    getRestrictLocationList();
  };

  const handleOpenDeleteLocation = (rowData) => {
    setLocationId(rowData?._id);
    setdeletelocationModal(true);
  };
  const handleCloseDeleteLocation = () => {
    setdeletelocationModal(false);
    getRestrictLocationList();
  };

  // Get Restriction list api
  const getRestrictLocationList = useCallback(() => {
    dispatch(showLoader("Loading please wait..."));
    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        setRow(res?.data?.data);
      } else {
        setRow([]);
      }
    };
    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || "Failed to fetch data",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
    };
    const params = {};
    if (demoaccount_id) {
      params.demoaccount_id = demoaccount_id;
    }
    LocationApi.getRestrictedServiceArea(params).then(onSuccess, onFailure);
  }, [demoaccount_id, dispatch]);

  useEffect(() => {
    getRestrictLocationList();
  }, [getRestrictLocationList]);

  // Toggle active/inactive status
  const handleToggleStatus = (rowData) => {
    dispatch(showLoader("Updating status..."));
    const updatedStatus = !rowData?.isActive;
    const data = { isActive: updatedStatus };
    
    const onSuccess = (res) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: res?.data?.message || "Status updated successfully",
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "success",
        })
      );
      getRestrictLocationList(); // Refresh data
    };
    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || "Failed to update status",
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "error",
        })
      );
    };
    LocationApi.restrictLocationUpdate(rowData?._id, data).then(onSuccess, onFailure);
  };

  const formatTime = (hour, minutes, timeFormat) => {
    let formattedHour = hour;
    let period = "";
    if (timeFormat === 12) {
      period = hour >= 12 ? "PM" : "AM";
      formattedHour = hour % 12 === 0 ? 12 : hour % 12;
    } else {
      period = hour >= 12 ? "PM" : "AM";
      formattedHour = hour % 12 === 0 ? 12 : hour % 12;
    }
    const formattedMinutes = minutes.toString().padStart(2, "0");
    return `${formattedHour}:${formattedMinutes} ${period}`;
  };

  const allDataRows = _.map(row || [], (data, index) => {
    const startingHour = data?.startingTime?.hour || 0;
    const startingMinutes = data?.startingTime?.minutes || 0;
    const startingTimeFormat = data?.startingTime?.timeFormat || 24;
    const startingTime = formatTime(startingHour, startingMinutes, startingTimeFormat);

    const endingHour = data?.endingTime?.hour || 0;
    const endingMinutes = data?.endingTime?.minutes || 0;
    const endingTimeFormat = data?.endingTime?.timeFormat || 24;
    const endingTime = formatTime(endingHour, endingMinutes, endingTimeFormat);

    return {
      sno: index + 1,
      location: data?.locationId?.locationName,
      country: data?.locationId?.countryId,
      restrictType: data?.restrictionType,
      status: data?.isActive ? "Active" : "Inactive",
      startingTime: startingTime,
      endingTime: endingTime,
      row: data,
    };
  });

  const totalRecords = allDataRows.length;
  const totalPagesCount = Math.ceil(totalRecords / pageSize) || 1;
  const dataRows = allDataRows.slice((page - 1) * pageSize, page * pageSize);

  const renderPagination = () => {
    let pages = [];
    const internalPage = page - 1; // 0-indexed for calc

    if (totalPagesCount <= 5) {
      for (let i = 0; i < totalPagesCount; i++) {
        pages.push(i);
      }
    } else {
      if (internalPage < 3) {
        pages = [0, 1, 2, "...", totalPagesCount - 1];
      } else if (internalPage > totalPagesCount - 4) {
        pages = [0, "...", totalPagesCount - 3, totalPagesCount - 2, totalPagesCount - 1];
      } else {
        pages = [0, "...", internalPage - 1, internalPage, internalPage + 1, "...", totalPagesCount - 1];
      }
    }

    return pages.map((item, index) => {
      if (item === "...") {
        return (
          <span key={index} className={classes.pageDots}>
            ...
          </span>
        );
      }

      return (
        <Button
          key={item}
          className={internalPage === item ? classes.pageButtonActive : classes.pageButton}
          onClick={() => setPage(item + 1)}
        >
          {item + 1}
        </Button>
      );
    });
  };

  return (
    <Box className={classes.root}>
      {/* Header Container */}
      <Box className={classes.headerContainer}>
        <Typography className={classes.pageTitle}>Restrict Location</Typography>
        <Typography className={classes.breadcrumb}>
          Admin / Zone Management / <span>Restrict Location</span>
        </Typography>
      </Box>

      {/* Actions */}
      <Box className={classes.actionRow} style={{ justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          className={classes.addCustomerButton}
          onClick={handleOpenAddRestrictServiceModal}
          startIcon={<AddLocationAltOutlined />}
        >
          Add Restricted Area
        </Button>
      </Box>

      {/* Data Table */}
      <TableContainer component={Paper} elevation={0} className={classes.tableWrapper}>
        <Table>
          <TableHead>
            <TableRow className={classes.tableHeaderRow}>
              <TableCell className={classes.tableHeaderCell} style={{ flex: 0.5 }}>
                S.No
              </TableCell>
              <TableCell className={classes.tableHeaderCell} style={{ flex: 1.5 }}>
                Location
              </TableCell>
              <TableCell className={classes.tableHeaderCell} style={{ flex: 1 }}>
                Starting Time
              </TableCell>
              <TableCell className={classes.tableHeaderCell} style={{ flex: 1 }}>
                Ending Time
              </TableCell>
              <TableCell className={classes.tableHeaderCell} style={{ flex: 1 }}>
                Restrict Type
              </TableCell>
              <TableCell className={classes.tableHeaderCell} style={{ flex: 1 }}>
                Country
              </TableCell>
              <TableCell className={classes.tableHeaderCell} style={{ flex: 1 }}>
                Status
              </TableCell>
              <TableCell className={classes.tableHeaderCell} style={{ flex: 1, textAlign: "right" }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {dataRows.map((rowItem) => (
              <TableRow key={rowItem.sno} className={classes.tableRow}>
                <TableCell className={classes.tableCell} style={{ flex: 0.5 }}>
                  {rowItem.sno}
                </TableCell>
                <TableCell className={`${classes.tableCell} ${classes.boldText}`} style={{ flex: 1.5 }}>
                  {rowItem.location}
                </TableCell>
                <TableCell className={classes.tableCell} style={{ flex: 1 }}>
                  {rowItem.startingTime}
                </TableCell>
                <TableCell className={classes.tableCell} style={{ flex: 1 }}>
                  {rowItem.endingTime}
                </TableCell>
                <TableCell className={classes.tableCell} style={{ flex: 1 }}>
                  {rowItem.restrictType}
                </TableCell>
                <TableCell className={classes.tableCell} style={{ flex: 1 }}>
                  {rowItem.country}
                </TableCell>
                <TableCell className={classes.tableCell} style={{ flex: 1 }}>
                  <span
                    onClick={() => handleToggleStatus(rowItem.row)}
                    style={{ cursor: "pointer" }}
                    className={
                      rowItem.status === "Active"
                        ? classes.statusBadgeActive
                        : classes.statusBadgeInactive
                    }
                  >
                    {rowItem.status}
                  </span>
                </TableCell>
                <TableCell className={classes.tableCell} style={{ flex: 1, textAlign: "right", display: "flex", justifyContent: "flex-end" }}>
                  <Box
                    component="span"
                    className={classes.actionButton}
                    onClick={() => handleOpenUpdateRestrictServiceModal(rowItem.row)}
                  >
                    <EditOutlined fontSize="small" /> Edit
                  </Box>
                  <Box
                    component="span"
                    className={classes.actionDeleteButton}
                    onClick={() => handleOpenDeleteLocation(rowItem.row)}
                  >
                    <DeleteOutline fontSize="small" /> Delete
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {dataRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} style={{ textAlign: "center", padding: "32px" }}>
                  <Typography variant="body2" color="textSecondary">
                    No records found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <Box className={classes.paginationContainer}>
          <Typography className={classes.paginationText}>
            Showing {(page - 1) * pageSize + (dataRows.length > 0 ? 1 : 0)} to{" "}
            {Math.min(page * pageSize, totalRecords)} of {totalRecords} results
          </Typography>

          <Box className={classes.paginationControls}>
            <ChevronLeft
              className={`${classes.pageArrow} ${page === 1 ? "disabled" : ""}`}
              onClick={() => page > 1 && setPage(page - 1)}
            />

            {renderPagination()}

            <ChevronRight
              className={`${classes.pageArrow} ${page === totalPagesCount ? "disabled" : ""}`}
              onClick={() => page < totalPagesCount && setPage(page + 1)}
            />
          </Box>
        </Box>
      </TableContainer>

      <RestrictedLocationAddModal
        open={RestrictLocationAddModal}
        handleClose={handleCloseAddRestrictServiceModal}
      />
      <RestrictedLocationUpdateModal
        open={RestrictLocationUpdateModal}
        handleClose={handleCloseUpdateRestrictServiceModal}
        locationId={locationId}
      />
      <DeleteLocationModal
        openDialog={deleteLocationModal}
        id={locationId}
        handleClose={handleCloseDeleteLocation}
      />
    </Box>
  );
};

export default RestrictLocation;
